import React from 'react';
import {
  Box,
  Card,
  Chip,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { getRequestDisplayData } from '../../utils/requestDisplay';

interface RequestInsuranceServiceSummaryProps {
  request: any;
  onClientClick?: (clientId: number) => void;
}

interface SummaryFieldProps {
  label: string;
  value: string;
  monospace?: boolean;
  highlight?: boolean;
}

const SummaryField: React.FC<SummaryFieldProps> = ({
  label,
  value,
  monospace = false,
  highlight = false,
}) => (
  <Box>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.7rem', display: 'block' }}
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        mt: 0.5,
        fontWeight: highlight ? 600 : 500,
        fontFamily: monospace ? 'monospace' : undefined,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      }}
    >
      {value || '-'}
    </Typography>
  </Box>
);

const RequestInsuranceServiceSummary: React.FC<RequestInsuranceServiceSummaryProps> = ({
  request,
  onClientClick,
}) => {
  const data = getRequestDisplayData(request);

  return (
    <Stack spacing={2}>
      <Card
        variant="outlined"
        sx={{
          p: 3,
          border: '2px solid',
          borderColor: 'primary.main',
          bgcolor: 'primary.50',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Typography variant="h6" fontWeight="bold" color="primary.main">
            Insurance Information
          </Typography>
          {data.hasInsuranceData ? (
            <Chip label="Insurance Client" size="small" color="primary" variant="outlined" />
          ) : (
            <Chip label="Limited insurance data" size="small" color="warning" variant="outlined" />
          )}
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' },
            gap: 2,
          }}
        >
          <SummaryField label="Insurance Company Name" value={data.insuranceCompanyName} highlight />
          <SummaryField label="Insurance Policy Number" value={data.policyNumber} monospace highlight />
          <SummaryField label="DARB Service Package" value={data.servicePackageName} highlight />
          <SummaryField label="Vehicle Chassis Number (VIN)" value={data.vinNumber} monospace highlight />
          <SummaryField label="Engine Number" value={data.engineNumber} monospace />
          <SummaryField label="Policy Start Date" value={data.policyStartDate} />
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.7rem', display: 'block' }}
            >
              Policy Expiry Date
            </Typography>
            <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2" fontWeight={600}>
                {data.policyExpiryDate}
              </Typography>
              {data.isPolicyExpired && <Chip label="Expired" color="error" size="small" />}
            </Box>
          </Box>
          {data.clientId && onClientClick && (
            <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
              <Link
                component="button"
                variant="body2"
                onClick={() => onClientClick(data.clientId!)}
                sx={{ fontWeight: 600 }}
              >
                View full client profile →
              </Link>
            </Box>
          )}
        </Box>
      </Card>

      <Card variant="outlined" sx={{ p: 3, border: '2px solid', borderColor: 'secondary.main', bgcolor: 'grey.50' }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 2, color: 'secondary.main' }}>
          Service & Location
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 2,
          }}
        >
          <Box sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.7rem', display: 'block' }}
            >
              Service Type Requested
            </Typography>
            <Chip
              label={data.serviceType}
              color="secondary"
              sx={{ mt: 0.75, fontWeight: 600 }}
            />
          </Box>
          <SummaryField label="Pickup Location" value={data.pickupLocation} highlight />
          <SummaryField label="Destination Location" value={data.destinationLocation} highlight />
        </Box>
      </Card>
    </Stack>
  );
};

export default RequestInsuranceServiceSummary;
